import { type ReactNode, useState } from 'react';
import {
    Bar,
    BarChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis,
} from 'recharts';

import {
    CHART_HEIGHT,
    ChartEmpty,
    ChartLegend,
    ChartTooltipFrame,
    formatBucketTick,
    formatBucketTitle,
    SpendChartCard,
} from '@apps/web/components/admin/llm_spend/chart_shared';
import {
    type SpendBucketSize,
    type SpendSeries,
} from '@apps/web/components/admin/llm_spend/constants';
import {
    formatUsd,
    formatUsdCompact,
} from '@apps/web/components/admin/llm_spend/spend_math';
import { SegmentedControl } from '@apps/web/components/ui/segmented_control';

export type StackedSpendPoint = {
    readonly bucketStart: string;
    readonly values: Readonly<Record<string, number>>;
};

// Dollars stacks absolute spend; share normalizes each bucket to 100% so a
// mix shift stays visible when volume moves.
export type StackedValueMode = 'usd' | 'share';

const VALUE_MODE_OPTIONS: ReadonlyArray<{
    readonly value: StackedValueMode;
    readonly label: string;
    readonly ariaLabel: string;
}> = [
    { value: 'usd', label: '$', ariaLabel: 'Dollars' },
    { value: 'share', label: '%', ariaLabel: 'Share of total' },
];

type StackedRow = {
    readonly bucketStart: string;
    readonly total: number;
    readonly [seriesKey: string]: number | string;
};

function formatPercentTick(value: number): string {
    return `${value.toFixed(0)}%`;
}

function StackedTooltip({
    active,
    label,
    payload,
    series,
    bucket,
    valueMode,
}: {
    readonly active?: boolean;
    readonly label?: string;
    readonly payload?: Array<{ readonly payload?: StackedRow }>;
    readonly series: ReadonlyArray<SpendSeries>;
    readonly bucket: SpendBucketSize;
    readonly valueMode: StackedValueMode;
}) {
    const row = payload?.[0]?.payload;
    if (!active || label === undefined || row === undefined) {
        return null;
    }
    // Largest stack segment first so the tooltip reads top-down like the bar.
    const rows = [...series].reverse().map((entry) => {
        const raw = row[entry.key];
        const value = typeof raw === 'number' ? raw : 0;
        return {
            key: entry.key,
            label: entry.label,
            color: entry.color,
            value:
                valueMode === 'usd' ? formatUsd(value) : `${value.toFixed(1)}%`,
        };
    });
    return (
        <ChartTooltipFrame
            title={formatBucketTitle(label, bucket)}
            rows={rows}
            footer={
                series.length > 1 ? (
                    <span className="flex w-full items-center justify-between">
                        <span>Total</span>
                        <span className="tabular-nums">
                            {formatUsd(row.total)}
                        </span>
                    </span>
                ) : undefined
            }
        />
    );
}

// Stacked dollars (or share of the bucket) per bucket. Series order is the
// stack order (first at the baseline). A surface-colored stroke separates
// segments instead of a border.
export function StackedSpendChart({
    points,
    series,
    bucket,
    valueMode = 'usd',
}: {
    readonly points: ReadonlyArray<StackedSpendPoint>;
    readonly series: ReadonlyArray<SpendSeries>;
    readonly bucket: SpendBucketSize;
    readonly valueMode?: StackedValueMode;
}) {
    if (points.length === 0 || series.length === 0) {
        return <ChartEmpty />;
    }
    const rows: Array<StackedRow> = points.map((point) => {
        const total = series.reduce(
            (acc, entry) => acc + (point.values[entry.key] ?? 0),
            0,
        );
        return {
            bucketStart: point.bucketStart,
            total,
            ...Object.fromEntries(
                series.map((entry) => {
                    const value = point.values[entry.key] ?? 0;
                    return [
                        entry.key,
                        valueMode === 'usd' || total === 0
                            ? value
                            : (value / total) * 100,
                    ];
                }),
            ),
        };
    });
    return (
        <div className="flex flex-col gap-2">
            <ChartLegend series={series} />
            <div className="w-full" style={{ height: CHART_HEIGHT }}>
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        data={rows}
                        margin={{ top: 4, left: 0, right: 8, bottom: 0 }}
                        barCategoryGap="20%"
                    >
                        <CartesianGrid
                            stroke="var(--border)"
                            vertical={false}
                        />
                        <XAxis
                            dataKey="bucketStart"
                            tick={{ fontSize: 11 }}
                            tickLine={false}
                            axisLine={{ stroke: 'var(--border)' }}
                            tickFormatter={formatBucketTick}
                            interval="preserveStartEnd"
                            minTickGap={28}
                        />
                        <YAxis
                            tick={{ fontSize: 11 }}
                            tickLine={false}
                            axisLine={false}
                            width={52}
                            domain={
                                valueMode === 'share' ? [0, 100] : [0, 'auto']
                            }
                            ticks={
                                valueMode === 'share'
                                    ? [0, 25, 50, 75, 100]
                                    : undefined
                            }
                            tickFormatter={
                                valueMode === 'share'
                                    ? formatPercentTick
                                    : formatUsdCompact
                            }
                        />
                        <RechartsTooltip
                            cursor={{ fill: 'var(--muted)', opacity: 0.5 }}
                            content={
                                <StackedTooltip
                                    series={series}
                                    bucket={bucket}
                                    valueMode={valueMode}
                                />
                            }
                        />
                        {series.map((entry) => (
                            <Bar
                                key={entry.key}
                                dataKey={entry.key}
                                name={entry.label}
                                stackId="spend"
                                fill={entry.color}
                                stroke="var(--background)"
                                strokeWidth={1}
                                isAnimationActive={false}
                            />
                        ))}
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

// A stacked chart in a card with its own dollars/share toggle. The toggle is
// per chart because the share view only makes sense for a part-to-whole
// stack, and the default is dollars.
export function StackedSpendCard({
    title,
    description,
    points,
    series,
    bucket,
    children,
}: {
    readonly title: string;
    readonly description?: string;
    readonly points: ReadonlyArray<StackedSpendPoint>;
    readonly series: ReadonlyArray<SpendSeries>;
    readonly bucket: SpendBucketSize;
    readonly children?: ReactNode;
}) {
    const [valueMode, setValueMode] = useState<StackedValueMode>('usd');
    return (
        <SpendChartCard
            title={title}
            description={description}
            action={
                <SegmentedControl
                    options={VALUE_MODE_OPTIONS}
                    value={valueMode}
                    onValueChange={setValueMode}
                    size="xs"
                    layout="auto"
                    aria-label={`${title}: dollars or share of total`}
                />
            }
        >
            <StackedSpendChart
                points={points}
                series={series}
                bucket={bucket}
                valueMode={valueMode}
            />
            {children}
        </SpendChartCard>
    );
}
